import { SUPPORTED_LOCALES, type Locale } from '@ecom/i18n/locales';
import { type ReactElement } from 'react';

import { cx } from '../utils/cx';

/**
 * Native names (endonyms), never run through `t()` — the international
 * convention for a language picker is that each option is labelled in
 * its OWN language, not translated into whichever locale happens to be
 * active (an English speaker looking for Arabic still needs to recognise
 * "العربية", not a French or English translation of the word "Arabic").
 */
const LANGUAGE_NAMES: Record<Locale, string> = {
  fr: 'Français',
  ar: 'العربية',
  en: 'English',
};

export interface LanguageSwitcherProps {
  readonly locale: Locale;
  readonly onChange: (locale: Locale) => void;
  readonly className?: string;
}

/**
 * Purely presentational — takes the active locale and a change handler
 * as props rather than reading `useI18n()`/deciding navigation itself,
 * because "changing the language" means something different in each
 * consumer: the storefront (Next.js) navigates to the equivalent
 * `/ar/...` path and must preserve the current page; the merchant and
 * admin dashboards (Vite SPAs) just update `useLocalePreference()`'s
 * local state. Both wire their own `onChange`; this component owns only
 * the three buttons and which one is marked current.
 */
export function LanguageSwitcher({
  locale,
  onChange,
  className,
}: LanguageSwitcherProps): ReactElement {
  return (
    <div
      role="group"
      aria-label="Langue / Language / اللغة"
      className={cx('flex gap-1', className)}
    >
      {SUPPORTED_LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          aria-pressed={locale === code}
          onClick={() => {
            if (code !== locale) onChange(code);
          }}
          className={cx(
            'ecom-lang__option rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors',
            locale === code
              ? 'bg-accentSoft text-accentInk'
              : 'text-inkFaint hover:bg-surfaceHover hover:text-ink',
          )}
        >
          {LANGUAGE_NAMES[code]}
        </button>
      ))}
    </div>
  );
}
